// ══════════════════════════════════════════════════════════════════════════
// Schedule Conflict Detection (mirrors backend/schedule_conflict.py)
// Two schedules only need explicit resolution if they overlap AND neither
// automatically outranks the other by specificity or priority.
// ══════════════════════════════════════════════════════════════════════════

function parseDate(value) {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function parseTimeToMinutes(value) {
  if (!value) return null;
  const [hour, minute] = value.split(':').map(Number);
  return hour * 60 + minute;
}

function parseDays(value) {
  if (!value) return null;
  return new Set(value.split(',').map(d => d.trim()).filter(Boolean));
}

function dateRangesOverlap(startA, endA, startB, endB) {
  const dStartA = parseDate(startA);
  const dEndA = parseDate(endA);
  const dStartB = parseDate(startB);
  const dEndB = parseDate(endB);

  const condition1 = dEndB === null || dStartA === null || dStartA <= dEndB;
  const condition2 = dEndA === null || dStartB === null || dStartB <= dEndA;

  return condition1 && condition2;
}

function daysOverlap(daysA, daysB) {
  const setA = parseDays(daysA);
  const setB = parseDays(daysB);

  if (setA === null || setB === null) return true;

  for (const day of setA) {
    if (setB.has(day)) return true;
  }
  return false;
}

function timeSegments(startMin, endMin) {
  if (startMin <= endMin) return [[startMin, endMin]];
  return [[startMin, 24 * 60], [0, endMin]];
}

function timesOverlap(startA, endA, startB, endB) {
  const minStartA = parseTimeToMinutes(startA);
  const minEndA = parseTimeToMinutes(endA);
  const minStartB = parseTimeToMinutes(startB);
  const minEndB = parseTimeToMinutes(endB);

  if (minStartA === null || minEndA === null) return true;
  if (minStartB === null || minEndB === null) return true;

  const segmentsA = timeSegments(minStartA, minEndA);
  const segmentsB = timeSegments(minStartB, minEndB);

  for (const [segAStart, segAEnd] of segmentsA) {
    for (const [segBStart, segBEnd] of segmentsB) {
      if (segAStart < segBEnd && segBStart < segAEnd) return true;
    }
  }
  return false;
}

export function schedulesConflict(scheduleA, scheduleB) {
  if (!dateRangesOverlap(
    scheduleA.effective_start_date, scheduleA.effective_end_date,
    scheduleB.effective_start_date, scheduleB.effective_end_date
  )) return false;

  if (!daysOverlap(scheduleA.days_of_week, scheduleB.days_of_week)) return false;

  return timesOverlap(
    scheduleA.start_time, scheduleA.end_time,
    scheduleB.start_time, scheduleB.end_time
  );
}

export function specificityScore(schedule) {
  let score = 0;
  if (schedule.effective_start_date || schedule.effective_end_date) score += 2;
  if (schedule.days_of_week) score += 1;
  return score;
}

// Returns 'a', 'b', or null (genuine tie)
export function rankCollidingSchedules(scheduleA, scheduleB) {
  const scoreA = specificityScore(scheduleA);
  const scoreB = specificityScore(scheduleB);

  if (scoreA > scoreB) return 'a';
  if (scoreB > scoreA) return 'b';

  const priorityA = scheduleA.priority || 0;
  const priorityB = scheduleB.priority || 0;

  if (priorityA > priorityB) return 'a';
  if (priorityB > priorityA) return 'b';

  return null;
}

function describeSchedule(schedule, tariffLookup) {
  const tariffName = tariffLookup[schedule.tariff_id] || `Tariff #${schedule.tariff_id}`;
  const parts = [tariffName];

  parts.push(schedule.days_of_week || 'all days');
  parts.push(schedule.start_time && schedule.end_time
    ? `${schedule.start_time}-${schedule.end_time}`
    : 'all hours');

  if (schedule.effective_start_date || schedule.effective_end_date) {
    const start = schedule.effective_start_date || 'the beginning';
    const end = schedule.effective_end_date || 'ongoing';
    parts.push(`from ${start} to ${end}`);
  }

  return parts.join(', ');
}

// Returns an array of unresolved conflicts: [{ indexA, indexB, message }]
export function findUnresolvedConflicts(schedules, tariffLookup) {
  const unresolved = [];
  for (let i = 0; i < schedules.length; i++) {
    for (let j = i + 1; j < schedules.length; j++) {
      if (!schedules[i].tariff_id || !schedules[j].tariff_id) continue;
      if (schedules[i].tariff_id === schedules[j].tariff_id) continue; // Same tariff — never a real conflict
      if (schedulesConflict(schedules[i], schedules[j])) {
        const winner = rankCollidingSchedules(schedules[i], schedules[j]);
        if (winner === null) {
          const descA = describeSchedule(schedules[i], tariffLookup);
          const descB = describeSchedule(schedules[j], tariffLookup);
          unresolved.push({
            indexA: i,
            indexB: j,
            message: `'${descA}' and '${descB}' overlap and neither is more specific. Choose which should take priority, or adjust the dates, days, or times.`
          });
        }
      }
    }
  }
  return unresolved;
}

// ══════════════════════════════════════════════════════════════════════════
// Gap detection — mirrors backend/schedule_conflict.py's find_coverage_gaps
// ══════════════════════════════════════════════════════════════════════════

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function formatMinutes(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function computeDayGapRanges(day, schedules) {
  const coveredSegments = [];

  for (const sched of schedules) {
    if (!sched.tariff_id) continue;
    const days = parseDays(sched.days_of_week);
    if (days !== null && !days.has(day)) continue;

    const startMin = parseTimeToMinutes(sched.start_time);
    const endMin = parseTimeToMinutes(sched.end_time);

    if (startMin === null || endMin === null) {
      coveredSegments.push([0, 24 * 60]);
    } else {
      coveredSegments.push(...timeSegments(startMin, endMin));
    }
  }

  if (coveredSegments.length === 0) {
    return ['FULL_DAY']; // Special marker: entire day uncovered
  }

  coveredSegments.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const [segStart, segEnd] of coveredSegments) {
    if (merged.length > 0 && segStart <= merged[merged.length - 1][1]) {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], segEnd);
    } else {
      merged.push([segStart, segEnd]);
    }
  }

  const dayGaps = [];
  let cursor = 0;
  for (const [segStart, segEnd] of merged) {
    if (segStart > cursor) {
      dayGaps.push(`${formatMinutes(cursor)}-${formatMinutes(segStart)}`);
    }
    cursor = Math.max(cursor, segEnd);
  }
  if (cursor < 24 * 60) {
    dayGaps.push(`${formatMinutes(cursor)}-24:00`);
  }

  return dayGaps;
}

export function findCoverageGaps(schedules) {
  if (!schedules || schedules.length === 0) return [];

  // Compute each day's gap pattern, then group days that share an identical pattern
  const dayPatterns = DAY_NAMES.map(day => ({
    day,
    gaps: computeDayGapRanges(day, schedules),
  }));

  const patternGroups = [];
  for (const { day, gaps } of dayPatterns) {
    if (gaps.length === 0) continue; // Fully covered, nothing to report
    const key = gaps.join('|');
    let group = patternGroups.find(g => g.key === key);
    if (!group) {
      group = { key, days: [], gaps };
      patternGroups.push(group);
    }
    group.days.push(day);
  }

  return patternGroups.map(group => {
    const dayLabel = group.days.length === 7
      ? 'Every day'
      : formatDayRange(group.days);

    if (group.gaps[0] === 'FULL_DAY') {
      return `${dayLabel}: no tariff assigned at all`;
    }
    return `${dayLabel}: no tariff assigned during ${group.gaps.join(', ')}`;
  });
}

function formatDayRange(days) {
  // Check if the days form a contiguous run in the standard week order
  const indices = days.map(d => DAY_NAMES.indexOf(d)).sort((a, b) => a - b);
  const isContiguous = indices.every((val, i) => i === 0 || val === indices[i - 1] + 1);

  if (isContiguous && indices.length > 1) {
    return `${DAY_NAMES[indices[0]]}-${DAY_NAMES[indices[indices.length - 1]]}`;
  }
  return days.join(', ');
}